import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext"; // Assuming AuthContext is used for managing authentication

const Login = () => {
  const { login, error } = useContext(AuthContext); // Assuming login function from AuthContext
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Handle login submission
  const handleLogin = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    setLoading(true);
    try {
      await login(credentials); // Call login function from context with the credentials
      navigate("/dashboard"); // Redirect to dashboard after successful login
    } catch (err) {
      const { status, errorMessage } = err; // Access status and errorMessage from the thrown error object
      if (status === 400 && errorMessage === "New password is required to complete the login process.") {
        // Redirect to force-password-change page
        navigate("/force-password-change", {
          state: {
            username: credentials.username,
            password: credentials.password, // Pass the original password as well
          },
        });
      } else {
        alert(errorMessage); // Display generic error if login fails
      }
    } finally {
      setLoading(false); // Set loading state to false after the process is completed
    }
  };


  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <img
          alt="Your Company"
          src={`${process.env.PUBLIC_URL}/assets/hive.png`}
          className="mx-auto h-20 w-auto"
        />
        <h2 className="mt-10 text-center text-2xl font-bold tracking-tight text-[#414141]">
          Sign in to your account
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        {/* Return Button */}
        <div className="mb-4">
          <Link
            to="/"
            className="text-sm font-medium text-[#f7b22d] hover:text-[#414141]"
          >
            &#8592; Return to Home
          </Link>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label
              htmlFor="username"
              className="block text-sm font-medium text-[#414141]"
            >
              Username
            </label>
            <div className="mt-2">
              <input
                id="username"
                name="username"
                type="text"
                required
                autoComplete="username"
                value={credentials.username}
                onChange={(e) =>
                  setCredentials({ ...credentials, username: e.target.value })
                }
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-[#414141] outline outline-1 -outline-offset-1 outline-[#f7b22d] placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-[#f7b22d] sm:text-sm"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[#414141]"
              >
                Password
              </label>
              <div className="text-sm">
                <Link
                  to="/forgot-password" // Redirect to ForgotPassword route
                  className="font-semibold text-[#f7b22d] hover:text-[#414141]"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            <div className="mt-2">
              <input
                id="password"
                name="password"
                type="password"
                required
                autoComplete="current-password"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-[#414141] outline outline-1 -outline-offset-1 outline-[#f7b22d] placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-[#f7b22d] sm:text-sm"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="flex w-full justify-center rounded-md bg-[#f7b22d] px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-[#414141] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f7b22d]"
              disabled={loading}
            >
              {loading ? "Logging in..." : "Sign in"}
            </button>
          </div>
        </form>

        {/* Display login error */}
        {error && <div className="alert alert-danger mt-3">{error}</div>}

        {/* Updated link to redirect to /contact-us */}
        <p className="mt-10 text-center text-sm text-gray-500">
          Not a member?{" "}
          <Link
            to="/contact-us" // Redirect to ContactUs route
            className="font-semibold text-[#f7b22d] hover:text-[#414141]"
          >
            Start a 3 months trial
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
